/* eslint-disable @typescript-eslint/no-explicit-any */
import { createStore, reconcile } from "solid-js/store";

import {
  postGetActivityConfig,
  postGetActivityDetails,
} from "@/api/clientApi/activity";
import { DialogType } from "@/components/Dialogs/types.ts";
import { useDialog } from "@/components/Dialogs/useDialog.ts";
import { getLangObject, useI18n } from "@/i18n";
import { formatDate } from "@/lib/dateUtils.ts";
import { isMobile } from "@/lib/isMobile";
import { renderInnerHtml } from "@/lib/utils.ts";
import {
  ClickType,
  DenyPackType,
  UserKycAuthActionType,
} from "@/types/compontent/base/baseEnum";
import {
  NewActivityCfg,
  NewActivityDetails,
  RepeatActivityCfg,
} from "@/types/swaggerex/activity";

import { KYCStore } from "./kycStore";
import { sysConfigStore } from "./sysConfigStore";
import { getUserChannel, userStore } from "./userStore";

// 类型定义
export type ActivityCfgMap = Record<number, NewActivityCfg>;
export type ActivityDetailsMap = Record<number, NewActivityDetails>;
export type RepeatActivityCfgMap = Record<number, RepeatActivityCfg>;

// 常量定义
const RULE_KEY = "1";
const DEFAULT_DENY_PACK_TYPE = 0;

// Store 初始化
const initialStore = {
  actConfigData: {} as ActivityCfgMap,
  actDetailsData: {} as ActivityDetailsMap,
  repeatActCfgData: {} as RepeatActivityCfgMap, //优惠活动
  bonusCategoryList: [] as number[],
  allOpenActivity: [] as number[],
  currentActivity: 0,
};

export const [activityStore, setActivityStore] = createStore(initialStore);

// Store 操作方法
export const setActivityCfgMap = (actConfigData: ActivityCfgMap) => {
  setActivityStore("actConfigData", reconcile(actConfigData));
  console.log("actConfigData", activityStore.actConfigData);
};

export const setRepeatActCfgDataMap = (actConfigData: RepeatActivityCfgMap) => {
  setActivityStore("repeatActCfgData", reconcile(actConfigData));
};
export const setAllOpenActivity = (allOpenActivity: number[]) => {
  setActivityStore("allOpenActivity", reconcile(allOpenActivity));
};

export const setActivityDetailsMap = (actDetailsData: ActivityDetailsMap) => {
  setActivityStore("actDetailsData", reconcile(actDetailsData));
};
// 更新指定details
export const setSingleActivityDetailsMap = (
  atype: number,
  details: NewActivityDetails,
) => {
  setActivityStore("actDetailsData", atype, reconcile(details));
};

export const setBonusCategoryList = (bonusCategoryList: number[]) => {
  setActivityStore("bonusCategoryList", reconcile(bonusCategoryList));
};

export const getActivityCfg = (atype: number): NewActivityCfg | undefined => {
  return activityStore.actConfigData[atype];
};
export const getRepeatActCfg = (
  atype: number,
): RepeatActivityCfg | undefined => {
  return activityStore.repeatActCfgData[atype];
};
export const getActivityDetails = (
  atype: number,
): NewActivityDetails | undefined => {
  return activityStore.actDetailsData[atype];
};

// 活动规则相关
export const getActRule = async (aType: number): Promise<string> => {
  const activityConfig = getActivityCfg(aType);
  if (!activityConfig) return "";

  const rules = getLangObject(activityConfig.rules) || "";
  const decryptedRules = rules;

  if (!decryptedRules || Object.keys(decryptedRules).length === 0) {
    try {
      const res = await postGetActivityConfig(aType);
      const newRules = res?.activity_cfg_list[0]?.rules || {};

      setActivityStore("actConfigData", (prev) => ({
        ...prev,
        [aType]: res?.activity_cfg_list[0],
      }));

      const rule = Object.values(newRules)[0];

      return (rule as string) ?? "";
    } catch (error) {
      console.error("Failed to fetch activity config:", error);
      return "";
    }
  }

  return decryptedRules;
};

export const addVipData = (cfg: any, key = "vip_cfg", vipKey = "vip_level") => {
  cfg[key] = cfg[key] || cfg["levels"] || cfg["vip_cfgs"] || [];
  const vipLength =
    sysConfigStore.sysConfigData?.vip_config?.levels?.length || 0;
  if (cfg[key].length < vipLength) {
    const list = new Array(vipLength).fill(null);
    cfg[key].forEach((el: any) => {
      const key = el[vipKey] - 1;
      if (key < vipLength) {
        list[key] = el;
      }
    });
    let copyItem: any = null;
    for (let i = 0; i < vipLength; i++) {
      const item = list[i];
      if (!item) {
        if (!copyItem) {
          list[i] = { [vipKey]: i + 1 };
        } else {
          copyItem[vipKey]++;
          list[i] = { ...copyItem };
        }
      } else {
        copyItem = JSON.parse(JSON.stringify(item));
      }
    }
    cfg[key] = list;
  }
};

// 状态检查工具函数
const checkDenyPackStatus = (
  denyPack: Array<unknown>,
  denyPackType: number,
  userChannelValue: unknown,
  userGeneralValue: unknown,
): boolean => {
  if (!denyPack?.length) return true;

  const statusMap = {
    [DenyPackType.ExcludeAgent]: () => !denyPack.includes(userGeneralValue),
    [DenyPackType.IncludeAgent]: () => denyPack.includes(userGeneralValue),
    [DenyPackType.ExcludeChannel]: () => !denyPack.includes(userChannelValue),
    [DenyPackType.IncludeChannel]: () => denyPack.includes(userChannelValue),
  };

  return statusMap[denyPackType as keyof typeof statusMap]?.() ?? true;
};

const checkLabelsDenyStatus = (
  denyPack: Array<unknown>,
  denyPackType: number,
  userLabels: Array<unknown>,
): boolean => {
  if (!userStore.isLogin) {
    return denyPackType !== DenyPackType.ExcludeAgent;
  }

  const labels = userLabels || [];

  if (denyPackType === DenyPackType.IncludeChannel) {
    return !labels.some((label) => denyPack.includes(label));
  }

  if (denyPackType === DenyPackType.ExcludeAgent) {
    return labels.some((label) => denyPack.includes(label));
  }

  return true;
};

// 活动状态检查
export const getActivityState = (aType: number): boolean => {
  // 特殊活动检查
  const config = getActivityCfg(aType);
  if (!config?.status) return false;

  const denyPack = config.deny_pack || [];
  const denyPackType = config.deny_pack_type || DEFAULT_DENY_PACK_TYPE;
  const { general, channel } = getUserChannel();

  // 检查 deny_pack 状态
  const denyPackStatus = checkDenyPackStatus(
    denyPack,
    denyPackType,
    channel,
    general,
  );

  if (!denyPackStatus) return false;

  // 检查 deny_labels 状态
  const denyLabels = config.deny_labels || [];
  const denyLabelType = config.deny_label_type || DEFAULT_DENY_PACK_TYPE;

  return checkLabelsDenyStatus(
    denyLabels,
    denyLabelType,
    userStore.userInfo?.labels || [],
  );
};
//获取活动信息
export const getActivityCfgMapData = async () => {
  const list = getAllOpenActivity();
  const details = await postGetActivityDetails({ atypes: list }, false);
  setActivityDetailsMap({ ...details?.activity_details });
};

export const doActivityFailed = (
  check_data,
  click: (click_id: ClickType, click_params: string) => void,
  callback?: () => void,
) => {
  const { t } = useI18n();
  const { showDialog, closeDialog } = useDialog();
  let content = "";
  let tipText = "";
  let confirmText = "";
  let cancelText = "";
  let clickTo = 0;
  const textSize = isMobile() ? "text-[12px]" : "text-[14px]";
  const gap = isMobile() ? "mt-[12px]" : "mt-[16px]";
  if (check_data.need_to_app) {
    clickTo = 1;
    content = t("common.go_app_claim");
    confirmText = t("common.install_app");
  } else if (
    check_data.need_bind_phone ||
    check_data.need_bind_email ||
    check_data.need_bind_withdraw_card ||
    check_data.need_kyc
  ) {
    clickTo = 2;
    content = t("common.complete_settings");
    confirmText = t("common.go_bind");
    cancelText = t("common.cancel");
    if (check_data.need_bind_phone) {
      tipText += "• " + t("common.phone_ver") + "<br/>";
    }
    if (check_data.need_bind_email) {
      tipText += "• " + t("common.email_ver") + "<br/>";
    }
    if (check_data.need_bind_withdraw_card) {
      tipText += "• " + t("common.bind_bank_claim") + "<br/>";
    }
    if (check_data.need_kyc) {
      tipText += "• " + t("settings.kyc_verification") + "<br/>";
    }

    if (
      !check_data.need_bind_phone &&
      !check_data.need_bind_email &&
      check_data.need_kyc
    ) {
      clickTo = 3;
    }
  } else if (check_data.receivable_at) {
    content = t("common.claim_after", {
      val: formatDate(check_data.receivable_at),
    });
    confirmText = t("common.got_it");
  }
  showDialog({
    type: DialogType.Tips,
    data: {
      title: t("common.tips"),
      content: `<div class="text-general-light text-[14px] ">${content}<div class="text-general-bright mt-[8px] ${textSize} text-left">${renderInnerHtml(tipText)}</div></div>`,
      confirmText: confirmText,
      onConfirm: () => {
        switch (clickTo) {
          case 1:
            // 下载
            closeDialog(DialogType.Tips);
            click(ClickType.Page, "/install");
            callback?.();
            break;
          case 2:
            // 设置
            closeDialog(DialogType.Tips);
            closeDialog(DialogType.ActivityIframe);
            click(ClickType.Page, "/settings?tab=security");
            callback?.();
            break;
          case 3:
            // 设置
            closeDialog(DialogType.Tips);
            closeDialog(DialogType.ActivityIframe);
            KYCStore.getUserKycAuthByAction(UserKycAuthActionType.bet);
            callback?.();
            break;
        }
      },
      cancelText: cancelText,
    },
  });
};

// 导出其他必要的函数
export const getAllOpenActivity = (): number[] =>
  Object.keys(activityStore.actConfigData).map(Number);

// 优惠活动
export const getRepeatActivity = (): number[] => {
  return Object.keys(activityStore.repeatActCfgData).map(Number);
};
